import { useState, useEffect } from "react";
import { useAppContext } from "@/providers";
import { t } from "@/lib/i18n/messages";
import {
  getWalkSessionStats,
  getWalkParticipantsDetail,
  getFlaggedSessions,
  getWalkRewardHistory,
} from "@/lib/api/analytics";
import type {
  WalkSessionStats,
  SessionParticipantDetail,
  FlaggedSession,
  WalkRewardHistoryEntry,
} from "@/types/analytics";
import type { Locale } from "@/types/auth";

interface WalkAnalyticsPanelProps {
  initiativeId: string;
}

type TabKey = "overview" | "participants" | "flagged" | "rewards";

export function WalkAnalyticsPanel({ initiativeId }: WalkAnalyticsPanelProps) {
  const { locale } = useAppContext();
  const isRTL = locale === "ar";
  const [activeTab, setActiveTab] = useState<TabKey>("overview");
  const [stats, setStats] = useState<WalkSessionStats | null>(null);
  const [participants, setParticipants] = useState<SessionParticipantDetail[]>(
    [],
  );
  const [flagged, setFlagged] = useState<FlaggedSession[]>([]);
  const [rewardHistory, setRewardHistory] = useState<WalkRewardHistoryEntry[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [initiativeId]);

  async function loadData() {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, participantsRes, flaggedRes, rewardHistoryRes] =
        await Promise.all([
          getWalkSessionStats(initiativeId),
          getWalkParticipantsDetail(initiativeId, { per_page: 20 }),
          getFlaggedSessions(initiativeId),
          getWalkRewardHistory(initiativeId, { per_page: 20 }),
        ]);

      if (statsRes.success && statsRes.data) setStats(statsRes.data);
      if (participantsRes.success && participantsRes.data)
        setParticipants(participantsRes.data);
      if (flaggedRes.success && flaggedRes.data) setFlagged(flaggedRes.data);
      if (rewardHistoryRes.success && rewardHistoryRes.data)
        setRewardHistory(rewardHistoryRes.data);
    } catch (e) {
      setError("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-blue" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        <p>{error}</p>
        <button
          onClick={loadData}
          className="mt-4 text-sm text-brand-blue underline"
        >
          {t(locale, "retry")}
        </button>
      </div>
    );
  }

  const tabs: { key: TabKey; label: string }[] = [
    { key: "overview", label: isRTL ? "نظرة عامة" : "Overview" },
    { key: "participants", label: t(locale, "participants") },
    { key: "flagged", label: isRTL ? "الجلسات المشبوهة" : "Flagged Sessions" },
    { key: "rewards", label: isRTL ? "سجل المكافآت" : "Reward History" },
  ];

  return (
    <div className="space-y-6" dir={isRTL ? "rtl" : "ltr"}>
      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === tab.key
                ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                : "text-gray-500 dark:text-gray-400 hover:text-gray-700"
            }`}
          >
            {tab.label}
            {tab.key === "flagged" && flagged.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
                {flagged.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "overview" && stats && (
        <OverviewTab stats={stats} locale={locale} />
      )}
      {activeTab === "participants" && (
        <ParticipantsTab participants={participants} locale={locale} />
      )}
      {activeTab === "flagged" && (
        <FlaggedTab sessions={flagged} locale={locale} />
      )}
      {activeTab === "rewards" && (
        <RewardHistoryTab entries={rewardHistory} locale={locale} />
      )}
    </div>
  );
}

function OverviewTab({
  stats,
  locale,
}: {
  stats: WalkSessionStats;
  locale: Locale;
}) {
  const isRTL = locale === "ar";

  const statCards = [
    {
      label: t(locale, "participants"),
      value: stats.total_participants,
      color: "text-brand-blue",
    },
    {
      label: isRTL ? "الجلسات" : "Sessions",
      value: stats.total_sessions,
      color: "text-brand-blue",
    },
    {
      label: isRTL ? "المكتملة" : "Completed",
      value: stats.completed_sessions,
      color: "text-brand-blue",
    },
    {
      label: isRTL ? "معدل الإكمال" : "Completion Rate",
      value: `${stats.completion_rate}%`,
      color: "text-brand-blue",
    },
    {
      label: t(locale, "totalSteps"),
      value: stats.total_steps.toLocaleString(),
      color: "text-brand-blue",
    },
    {
      label: t(locale, "totalDistance"),
      value: `${(stats.total_distance_meters / 1000).toFixed(1)} km`,
      color: "text-brand-blue",
    },
    {
      label: t(locale, "totalRewardsDistributed"),
      value: `${stats.total_rewards_distributed} NC`,
      color: "text-brand-blue",
    },
    {
      label: t(locale, "budgetRemaining"),
      value: `${stats.budget_remaining} NC`,
      color: "text-brand-blue",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((card, i) => (
          <div
            key={i}
            className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700"
          >
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              {card.label}
            </p>
            <p className={`text-xl font-bold ${card.color}`}>{card.value}</p>
          </div>
        ))}
      </div>

      {/* Session breakdown */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
          {isRTL ? "توزيع الجلسات" : "Session Breakdown"}
        </h4>
        <div className="space-y-3">
          <ProgressRow
            label={isRTL ? "مكتملة" : "Completed"}
            value={stats.completed_sessions}
            total={stats.total_sessions}
            color="bg-green-500"
          />
          <ProgressRow
            label={isRTL ? "نشطة" : "Active"}
            value={stats.active_sessions}
            total={stats.total_sessions}
            color="bg-blue-500"
          />
          <ProgressRow
            label={isRTL ? "مشبوهة" : "Flagged"}
            value={stats.flagged_sessions}
            total={stats.total_sessions}
            color="bg-red-500"
          />
          <ProgressRow
            label={isRTL ? "متروكة" : "Abandoned"}
            value={stats.abandoned_sessions}
            total={stats.total_sessions}
            color="bg-gray-400"
          />
        </div>
      </div>

      {/* Budget usage */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          {t(locale, "budgetUsage")}
        </h4>
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
              <div
                className="bg-brand-blue h-3 rounded-full transition-all"
                style={{
                  width: `${Math.min(stats.budget_used_percentage, 100)}%`,
                }}
              />
            </div>
          </div>
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 min-w-[48px] text-right">
            {stats.budget_used_percentage}%
          </span>
        </div>
      </div>
    </div>
  );
}

function ProgressRow({
  label,
  value,
  total,
  color,
}: {
  label: string;
  value: number;
  total: number;
  color: string;
}) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-gray-600 dark:text-gray-400 w-24">
        {label}
      </span>
      <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-2">
        <div
          className={`${color} h-2 rounded-full`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 w-10 text-right">
        {value}
      </span>
    </div>
  );
}

function ParticipantsTab({
  participants,
  locale,
}: {
  participants: SessionParticipantDetail[];
  locale: Locale;
}) {
  const isRTL = locale === "ar";
  const [expanded, setExpanded] = useState<string | null>(null);

  if (participants.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        {isRTL ? "لا يوجد مشاركون بعد" : "No participants yet"}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {participants.map((p) => (
        <div
          key={p.user_id}
          className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
        >
          <button
            onClick={() =>
              setExpanded(expanded === p.user_id ? null : p.user_id)
            }
            className="w-full p-4 flex items-center gap-4 text-left hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
          >
            <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center overflow-hidden">
              {p.avatar ? (
                <img
                  src={p.avatar}
                  alt={p.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-sm font-bold text-gray-500">
                  {p.name?.charAt(0)}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 dark:text-white truncate">
                {p.name}
              </p>
              <p className="text-xs text-gray-500">
                {p.total_sessions} {isRTL ? "جلسات" : "sessions"} ·{" "}
                {p.total_steps.toLocaleString()} {isRTL ? "خطوة" : "steps"}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-green-600">
                {p.total_rewards_earned} NC
              </p>
              {p.flagged_sessions > 0 && (
                <p className="text-xs text-red-500">
                  {p.flagged_sessions} {isRTL ? "مشبوهة" : "flagged"}
                </p>
              )}
            </div>
            <svg
              className={`w-5 h-5 text-gray-400 transition-transform ${expanded === p.user_id ? "rotate-180" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {expanded === p.user_id && p.sessions.length > 0 && (
            <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-3 space-y-2 bg-gray-50 dark:bg-gray-850">
              {p.sessions.map((s) => (
                <div
                  key={s.session_id}
                  className="flex items-center gap-3 text-sm"
                >
                  <span
                    className={`inline-block w-2 h-2 rounded-full ${
                      s.status === "completed"
                        ? "bg-green-500"
                        : s.status === "flagged"
                          ? "bg-red-500"
                          : s.status === "active"
                            ? "bg-blue-500"
                            : "bg-gray-400"
                    }`}
                  />
                  <span className="text-gray-600 dark:text-gray-400 flex-1">
                    {s.total_steps} {isRTL ? "خطوة" : "steps"} ·{" "}
                    {(s.total_distance_meters / 1000).toFixed(1)} km
                  </span>
                  <span className="text-gray-400 text-xs">
                    {s.started_at
                      ? new Date(s.started_at).toLocaleDateString()
                      : ""}
                  </span>
                  {s.reward_amount && s.reward_status === "credited" && (
                    <span className="text-green-600 font-medium">
                      +{s.reward_amount} NC
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function RewardHistoryTab({
  entries,
  locale,
}: {
  entries: WalkRewardHistoryEntry[];
  locale: Locale;
}) {
  const isRTL = locale === "ar";

  // Ensure entries is always an array
  const safeEntries = Array.isArray(entries) ? entries : [];

  if (safeEntries.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        {isRTL ? "لا يوجد سجل مكافآت بعد" : "No reward history yet"}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {safeEntries.map((entry) => (
        <div
          key={entry.id}
          className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center overflow-hidden flex-shrink-0">
              {entry.user_avatar ? (
                <img
                  src={entry.user_avatar}
                  alt={entry.user_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-xs font-bold text-gray-500">
                  {entry.user_name?.charAt(0)}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 dark:text-white text-sm truncate">
                {entry.user_name}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {entry.steps_this_session.toLocaleString()}{" "}
                {isRTL ? "خطوة" : "steps"} ·{" "}
                {new Date(entry.created_at).toLocaleDateString()}
              </p>
            </div>
            <div className="text-right flex-shrink-0">
              <p
                className={`text-sm font-semibold ${entry.reward_status === "credited" ? "text-green-600" : "text-gray-400"}`}
              >
                {entry.reward_status === "credited"
                  ? `+${entry.green_points_earned} GP`
                  : isRTL
                    ? "غير مؤهل"
                    : "Not eligible"}
              </p>
              {entry.newly_completed_goals > 0 && (
                <p className="text-xs text-emerald-600">
                  {entry.newly_completed_goals} {isRTL ? "هدف" : "goal(s)"}
                </p>
              )}
            </div>
          </div>
          <div className="mt-3">
            <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-1.5">
              <div
                className="bg-emerald-500 h-1.5 rounded-full"
                style={{
                  width: `${Math.min(entry.progress_percentage, 100)}%`,
                }}
              />
            </div>
            <p className="text-xs text-gray-400 mt-1">
              {entry.progress_percentage.toFixed(1)}%{" "}
              {isRTL ? "نحو الهدف التالي" : "towards next goal"}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

function FlaggedTab({
  sessions,
  locale,
}: {
  sessions: FlaggedSession[];
  locale: Locale;
}) {
  const isRTL = locale === "ar";

  if (sessions.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        <div className="text-4xl mb-3">✓</div>
        <p className="font-medium">
          {isRTL ? "لا توجد جلسات مشبوهة" : "No flagged sessions"}
        </p>
        <p className="text-sm mt-1">
          {isRTL
            ? "جميع الجلسات تبدو طبيعية"
            : "All sessions appear legitimate"}
        </p>
      </div>
    );
  }

  const checkTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      step_gps_correlation: isRTL
        ? "ارتباط الخطوات/GPS"
        : "Step-GPS Correlation",
      teleportation: isRTL ? "انتقال آني" : "Teleportation",
      speed: isRTL ? "سرعة مفرطة" : "Excessive Speed",
      sensor_pattern: isRTL ? "نمط مكرر" : "Sensor Pattern",
      path_deviation: isRTL ? "انحراف عن المسار" : "Path Deviation",
    };
    return labels[type] || type;
  };

  return (
    <div className="space-y-4">
      {sessions.map((s) => (
        <div
          key={s.session_id}
          className="bg-white dark:bg-gray-800 rounded-xl border border-red-200 dark:border-red-900 p-5"
        >
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              {s.user_avatar ? (
                <img
                  src={s.user_avatar}
                  alt={s.user_name}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <span className="text-sm font-bold text-red-600">
                  {s.user_name?.charAt(0)}
                </span>
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <p className="font-medium text-gray-900 dark:text-white">
                  {s.user_name}
                </p>
                <span className="text-sm font-semibold text-red-600">
                  {isRTL ? "درجة الاحتيال" : "Fraud Score"}: {s.fraud_score}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {s.total_steps} {isRTL ? "خطوة" : "steps"} ·{" "}
                {(s.total_distance_meters / 1000).toFixed(2)} km ·{" "}
                {s.started_at ? new Date(s.started_at).toLocaleString() : ""}
              </p>

              {s.fraud_checks.length > 0 && (
                <div className="mt-3 space-y-2">
                  {s.fraud_checks.map((check, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 text-sm bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2"
                    >
                      <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                      <span className="font-medium text-red-700 dark:text-red-400">
                        {checkTypeLabel(check.check_type)}
                      </span>
                      <span className="text-red-500 text-xs">
                        +{check.score_delta}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
